import api from "./api.service";

export interface ChargeCalculationLine {
  id: string;
  calculationId: string;
  invoiceId: string | null;
  expenseCategoryId: string | null;
  providerName: string | null;
  label: string | null;
  expenseCategoryName: string | null;
  invoiceAmount: number | null;
  periodStartDate: string | null;
  periodEndDate: string | null;
  invoiceDate: string | null;
  daysInYear: number | null;
  totalPeriodDays: number | null;
  prorataRatio: number | null;
  amount: number;
  isManual: boolean;
  isAdjusted: boolean;
  originalAmount: number | null;
  dateCreated: string | null;
}

export type CalculationMode = "prorata" | "invoice_date";

export interface ChargeCalculation {
  id: string;
  colocId: string;
  year: number;
  totalAmount: number;
  status: string;
  calculationMode: CalculationMode;
  notes: string | null;
  dateCreated: string | null;
  dateModified: string | null;
  lines: ChargeCalculationLine[];
}

export interface ChargeCalculationSummary {
  id: string;
  colocId: string;
  year: number;
  totalAmount: number;
  status: string;
  calculationMode: CalculationMode;
  notes: string | null;
  dateCreated: string | null;
  dateModified: string | null;
}

class ChargeCalculationsService {
  async listByColoc(colocId: string): Promise<ChargeCalculationSummary[]> {
    const response = await api.get(`/charge-calculations/coloc/${colocId}`);
    return response.data;
  }

  async getById(calculationId: string): Promise<ChargeCalculation> {
    const response = await api.get(`/charge-calculations/${calculationId}`);
    return response.data;
  }

  async create(colocId: string, year: number, notes?: string): Promise<ChargeCalculation> {
    const response = await api.post("/charge-calculations/", {
      colocId,
      year,
      notes: notes || null,
    });
    return response.data;
  }

  async recalculate(calculationId: string): Promise<ChargeCalculation> {
    const response = await api.put(`/charge-calculations/${calculationId}/recalculate`);
    return response.data;
  }

  async finalize(calculationId: string): Promise<ChargeCalculation> {
    const response = await api.put(`/charge-calculations/${calculationId}/finalize`);
    return response.data;
  }

  async unfinalize(calculationId: string): Promise<ChargeCalculation> {
    const response = await api.put(`/charge-calculations/${calculationId}/unfinalize`);
    return response.data;
  }

  async updateLine(
    calculationId: string,
    lineId: string,
    data: { amount?: number; label?: string }
  ): Promise<ChargeCalculationLine> {
    const response = await api.patch(
      `/charge-calculations/${calculationId}/lines/${lineId}`,
      data
    );
    return response.data;
  }

  async addManualLine(
    calculationId: string,
    data: {
      label: string;
      amount: number;
      providerName?: string;
      expenseCategoryName?: string;
      expenseCategoryId?: string;
    }
  ): Promise<ChargeCalculationLine> {
    const response = await api.post(
      `/charge-calculations/${calculationId}/lines`,
      data
    );
    return response.data;
  }

  async removeLine(calculationId: string, lineId: string): Promise<void> {
    await api.delete(`/charge-calculations/${calculationId}/lines/${lineId}`);
  }

  async deleteCalculation(calculationId: string): Promise<void> {
    await api.delete(`/charge-calculations/${calculationId}`);
  }

  async getAvailableInvoices(calculationId: string): Promise<AvailableInvoice[]> {
    const response = await api.get(`/charge-calculations/${calculationId}/available-invoices`);
    return response.data;
  }

  async addInvoiceToCalculation(calculationId: string, invoiceId: string): Promise<ChargeCalculationLine> {
    const response = await api.post(`/charge-calculations/${calculationId}/add-invoice`, { invoiceId });
    return response.data;
  }
}

export interface AvailableInvoice {
  invoiceId: string;
  fileName: string;
  providerName: string | null;
  invoiceAmount: number;
  invoiceDate: string | null;
  periodStartDate: string | null;
  periodEndDate: string | null;
  prorataRatioForYear: number;
  availableRatio: number;
  availableAmount: number;
  amountForYear: number;
}

export const chargeCalculationsService = new ChargeCalculationsService();
